import { Link, useParams, Navigate } from "react-router-dom";
import { usePageMeta } from "../lib/seo";
import { PROJECTS, getProject } from "../data/projects";
import { Figure } from "../components/site/Figure";

const buildRows = (photos) => {
  const rows = [];
  let i = 0;
  let stdPhase = 0;
  while (i < photos.length) {
    const p = photos[i];
    if (p.tier === "feature") {
      rows.push([{ photo: p }]);
      i += 1;
    } else if (p.tier === "archival") {
      let run = 0;
      while (i + run < photos.length && photos[i + run].tier === "archival") run += 1;
      if (run === 1) {
        const next = photos[i + 1];
        rows.push(next ? [{ photo: p }, { photo: next }] : [{ photo: p }]);
        i += next ? 2 : 1;
      } else {
        const take = run % 3 === 1 ? 2 : Math.min(3, run);
        rows.push(photos.slice(i, i + take).map((ph) => ({ photo: ph })));
        i += take;
      }
    } else {
      const next = photos[i + 1];
      if (stdPhase % 2 === 1 && next && next.tier === "standard") {
        rows.push([{ photo: p }, { photo: next }]);
        i += 2;
      } else {
        rows.push([{ photo: p, max: stdPhase === 2 ? "max-w-[480px]" : undefined }]);
        i += 1;
      }
      stdPhase = (stdPhase + 1) % 4;
    }
  }
  return rows;
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = getProject(slug);

  usePageMeta(
    project
      ? `${project.name} — Sandquist Construction`
      : "Projects | Sandquist Construction",
    project ? project.caption : undefined
  );

  if (!project) return <Navigate to="/projects" replace />;

  const idx = PROJECTS.findIndex((p) => p.slug === slug);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];
  const rows = buildRows(project.photos);

  return (
    <main className="max-w-[1200px] mx-auto px-6 lg:px-8" data-testid="project-detail-page">
      <div className="pt-16 sm:pt-24 pb-14 max-w-3xl">
        <Link to="/projects" className="font-label text-xs uppercase tracking-[0.18em] text-plaster hover:text-ink transition-colors duration-200" data-testid="back-to-projects-link">
          ← All projects
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mt-6" data-testid="project-name">{project.name}</h1>
        <p className="text-sm text-inkmuted mt-4" data-testid="project-location">{project.location}</p>
        <p className="text-sm text-inkmuted mt-1" data-testid="project-architect">Designed by David S. Wilson of WA Design.</p>
      </div>

      <div className="space-y-14 sm:space-y-20 pb-16" data-testid="project-gallery">
        {rows.map((row, r) => (
          <div key={r} className={row.length > 1 ? "flex flex-col sm:flex-row gap-8 lg:gap-10 items-start" : ""}>
            {row.map(({ photo, max }) => (
              <Figure
                key={photo.src}
                src={photo.src}
                alt={photo.alt}
                tier={photo.tier}
                max={max}
                caption={`${project.name} — ${project.location}`}
                className={row.length > 1 ? "min-w-0 flex-1" : ""}
                testId={`project-photo-${project.photos.indexOf(photo) + 1}`}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="border-t border-hairline py-12 flex justify-end">
        <Link to={`/projects/${next.slug}`} className="group text-right" data-testid="next-project-link">
          <p className="font-label text-xs uppercase tracking-[0.18em] text-inkmuted">Next project</p>
          <p className="font-display text-2xl text-ink mt-2 group-hover:text-plaster transition-colors duration-200">{next.name}</p>
        </Link>
      </div>
    </main>
  );
}
